import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, type ReactNode } from "react";
import { configureApiAuth } from "@/api/client";
import { AUTH_ENABLED } from "./config";

/** Hands the API client a token getter backed by Auth0's silent token fetch; a failed
 * fetch yields no token (the request then goes out unauthenticated and the backend's
 * 401 surfaces as a page error) rather than throwing. Renders nothing until auth0-react
 * has finished initializing, so no page fires a request before a token can be had. */
function AuthenticatedBridge({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, isLoading } = useAuth0();

  useEffect(() => {
    configureApiAuth(async () => {
      try {
        return (await getAccessTokenSilently()) ?? null;
      } catch {
        return null;
      }
    });
  }, [getAccessTokenSilently]);

  if (isLoading) return null;
  return <>{children}</>;
}

/** Wires the Auth0 access-token getter into the API client once auth0-react has
 * initialized. Kept separate from AuthProvider so it can sit inside the router (it
 * needs no navigation context, just `useAuth0`), rendered once near the app root.
 * When Auth0 isn't configured (see ./config.ts) there is no Auth0Provider in the tree
 * to call `useAuth0` against, so every request just goes out with no bearer token. */
export function ApiTokenBridge({ children }: { children: ReactNode }) {
  if (!AUTH_ENABLED) return <>{children}</>;
  return <AuthenticatedBridge>{children}</AuthenticatedBridge>;
}
